import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { MENU_ITEMS } from '../pages/pages-menu';
import { NbMenuItem, NbIconConfig } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';

export interface RouteItem {
  title: string;
  translationKey?: string;
  link: string;
  icon?: string | NbIconConfig;
  breadcrumb?: string[];
}

const SPOTLIGHT_RECENT_ROUTES_KEY = 'spotlight_recent_routes';
const SPOTLIGHT_RECENT_ROUTES_LIMIT = 3;

@Injectable({
  providedIn: 'root'
})
export class SpotlightSearchService {
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  public isOpen$: Observable<boolean> = this.isOpenSubject.asObservable();

  private allRoutes: RouteItem[] = [];
  private recentRoutes: RouteItem[] = [];

  constructor(
    private router: Router,
    private translate: TranslateService
  ) {
    this.initializeRoutes();
    this.loadRecentRoutesFromStorage();
  }

  private initializeRoutes(): void {
    this.allRoutes = this.flattenMenuItems(MENU_ITEMS);
  }

  private loadRecentRoutesFromStorage(): void {
    try {
      const stored = localStorage.getItem(SPOTLIGHT_RECENT_ROUTES_KEY);
      if (!stored) {
        this.recentRoutes = [];
        return;
      }
      const links: string[] = JSON.parse(stored);
      if (!Array.isArray(links)) {
        this.recentRoutes = [];
        return;
      }
      this.recentRoutes = links
        .map(link => this.allRoutes.find(r => r.link === link))
        .filter((r): r is RouteItem => !!r);
    } catch {
      this.recentRoutes = [];
    }
  }

  private persistRecentRoutes(): void {
    const links = this.recentRoutes.map(r => r.link);
    try {
      localStorage.setItem(SPOTLIGHT_RECENT_ROUTES_KEY, JSON.stringify(links));
    } catch {
      // ignore storage errors
    }
  }

  private addRecentRoute(link: string): void {
    const route = this.allRoutes.find(r => r.link === link);
    if (!route) {
      return;
    }

    // Remove if already present
    this.recentRoutes = this.recentRoutes.filter(r => r.link !== link);
    // Add to front
    this.recentRoutes.unshift(route);
    // Cap to limit
    if (this.recentRoutes.length > SPOTLIGHT_RECENT_ROUTES_LIMIT) {
      this.recentRoutes = this.recentRoutes.slice(0, SPOTLIGHT_RECENT_ROUTES_LIMIT);
    }

    this.persistRecentRoutes();
  }

  private flattenMenuItems(items: NbMenuItem[], breadcrumb: string[] = []): RouteItem[] {
    const routes: RouteItem[] = [];

    items.forEach(item => {
      const currentBreadcrumb = [...breadcrumb];
      
      if (item.title) {
        currentBreadcrumb.push(item.title);
      }

      if (item.link && !item.hidden) {
        routes.push({
          title: item.title || '',
          translationKey: item.title,
          link: item.link,
          icon: item.icon,
          breadcrumb: currentBreadcrumb
        });
      }

      if (item.children && item.children.length > 0) {
        routes.push(...this.flattenMenuItems(item.children, currentBreadcrumb));
      }
    });

    return routes;
  }

  public searchRoutes(query: string): RouteItem[] {
    if (!query || query.trim() === '') {
      // No query: show recent routes first (from localStorage), then the rest
      const translatedRecent = this.recentRoutes.map(route => ({
        ...route,
        title: route.translationKey
          ? this.translate.instant(route.translationKey)
          : route.title,
      }));

      const remainingCapacity = Math.max(10 - translatedRecent.length, 0);

      const translatedRest = this.allRoutes
        .filter(r => !this.recentRoutes.some(rr => rr.link === r.link))
        .slice(0, remainingCapacity)
        .map(route => ({
          ...route,
          title: route.translationKey
            ? this.translate.instant(route.translationKey)
            : route.title,
        }));

      return [...translatedRecent, ...translatedRest];
    }

    const lowerQuery = query.toLowerCase();
    
    return this.allRoutes
      .map(route => {
        // Get translated title
        const translatedTitle = route.translationKey 
          ? this.translate.instant(route.translationKey)
          : route.title;

        // Calculate relevance score
        let score = 0;
        const lowerTitle = translatedTitle.toLowerCase();
        const lowerOriginalTitle = route.title.toLowerCase();
        
        // Exact match (highest priority)
        if (lowerTitle === lowerQuery || lowerOriginalTitle === lowerQuery) {
          score = 1000;
        }
        // Starts with query
        else if (lowerTitle.startsWith(lowerQuery) || lowerOriginalTitle.startsWith(lowerQuery)) {
          score = 500;
        }
        // Contains query
        else if (lowerTitle.includes(lowerQuery) || lowerOriginalTitle.includes(lowerQuery)) {
          score = 250;
        }
        // Check breadcrumb
        else if (route.breadcrumb) {
          const breadcrumbMatch = route.breadcrumb.some(crumb => {
            const translatedCrumb = this.translate.instant(crumb).toLowerCase();
            return translatedCrumb.includes(lowerQuery) || crumb.toLowerCase().includes(lowerQuery);
          });
          if (breadcrumbMatch) {
            score = 100;
          }
        }

        return { ...route, score, translatedTitle };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10) // Limit to 10 results
      .map(({ score, translatedTitle, ...route }) => ({ ...route, title: translatedTitle }));
  }

  public navigateToRoute(link: string): void {
    this.addRecentRoute(link);
    this.router.navigate([link]);
    this.close();
  }

  public open(): void {
    this.isOpenSubject.next(true);
  }

  public close(): void {
    this.isOpenSubject.next(false);
  }

  public toggle(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  public getAllRoutes(): RouteItem[] {
    return this.allRoutes.map(route => ({
      ...route,
      title: route.translationKey 
        ? this.translate.instant(route.translationKey)
        : route.title
    }));
  }
}

